import React, { useMemo } from 'react';
import type { TranslationSegment } from './translation.types';
import { InlineTable } from './InlineTable';
import { MIN_FIELD_WIDTH, CHAR_WIDTH_MULTIPLIER } from '@/run-stage/utils/translation.constants';
import { ConstituentBracket } from './ConstituentBracket';

interface SegmentRendererProps {
  segment: TranslationSegment;
  nodeId: string;
  highlighted?: boolean;
  onParameterChanged?: (nodeId: string, paramName: string, value: unknown) => void;
  readOnly?: boolean;
}

/**
 * Error fallback for unhandled segment types
 */
const UnhandledSegmentFallback: React.FC<{ kind: string }> = ({ kind }) => (
  <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>
    [Unhandled segment type: {kind}]
  </span>
);

/**
 * Calculate optimal field width based on content
 */
const calculateFieldWidth = (value: unknown): number => {
  const strValue = formatInlineValue(value).length;
  return Math.max(MIN_FIELD_WIDTH, strValue * CHAR_WIDTH_MULTIPLIER);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatInlineValue = (value: unknown, depth = 0): string => {
  if (depth > 6) return '';
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => formatInlineValue(item, depth + 1))
      .filter((item) => item.trim() !== '')
      .join(', ');
  }

  if (!isRecord(value)) return String(value);

  if (typeof value.label === 'string' && value.label.trim() !== '') {
    return value.label;
  }

  if ((value.amount != null || value.value != null) && (value.unit != null || value.units != null)) {
    const amountText = formatInlineValue(value.amount ?? value.value, depth + 1);
    const unitText = formatInlineValue(value.unit ?? value.units, depth + 1);
    return `${amountText} ${unitText}`.trim();
  }

  if (value.minValue != null || value.maxValue != null || value.min != null || value.max != null) {
    const minText = formatInlineValue(value.minValue ?? value.min, depth + 1) || '—';
    const maxText = formatInlineValue(value.maxValue ?? value.max, depth + 1) || '—';
    return `${minText} - ${maxText}`;
  }

  if ('value' in value) {
    const nested = formatInlineValue(value.value, depth + 1);
    if (nested.trim() !== '') return nested;
  }

  if ('parameters' in value) {
    const nested = formatInlineValue(value.parameters, depth + 1);
    if (nested.trim() !== '') return nested;
  }

  return Object.entries(value)
    .filter(([key]) => !['parameterId', 'parameterName', 'required', 'mandatory'].includes(key))
    .map(([key, item]) => {
      const text = formatInlineValue(item, depth + 1);
      return text.trim() !== '' ? `${key}: ${text}` : '';
    })
    .filter((part) => part !== '')
    .join(', ');
};

/**
 * Memoized text segment renderer
 */
const TextSegment: React.FC<{ value: string }> = ({ value }) => <span>{value}</span>;

/**
 * Memoized material reference renderer
 */
const MaterialRefSegment: React.FC<{
  materialName: string;
  containerName?: string;
  constituentExpr?: string;
  constituents?: Array<{ name: string; weight: number }>;
  showConstituentToggle?: boolean;
  renderVariant?: 'default' | 'transfer-source' | 'transfer-destination';
}> = ({
  materialName,
  containerName,
  constituentExpr,
  constituents = [],
  showConstituentToggle = true,
  renderVariant = 'default',
}) => {
  const showContainer = Boolean(containerName) && String(containerName).trim() !== '';
  const labelText =
    renderVariant === 'transfer-source'
      ? `${materialName}${showContainer ? ` from ${containerName} container` : ''}`
      : renderVariant === 'transfer-destination'
        ? `${showContainer ? `${containerName} container` : materialName}`
        : `${materialName}${showContainer ? ` container ${containerName}` : ''}`;
  
  return (
    <span style={{ fontWeight: 500 }}>
      {labelText}
      {showConstituentToggle ? (
        <ConstituentBracket
          constituents={constituents}
          collapsedExpr={constituentExpr}
          collapsedMode="icon"
          showExpandedWeights={false}
        />
      ) : null}
    </span>
  );
};

/**
 * Field input renderer — always read-only on protocol page
 */
const FieldSegmentRenderer: React.FC<{
  segment: any;
  highlighted: boolean;
  width: number;
}> = ({ segment, highlighted, width }) => (
  <input
    type={segment.fieldType === 'number' ? 'number' : 'text'}
    className={`inline-field${highlighted ? ' highlighted' : ''} read-only`}
    value={formatInlineValue(segment.value)}
    disabled
    readOnly
    aria-label={segment.paramName ? `${segment.paramName} (read-only)` : 'Parameter (read-only)'}
    style={{
      minWidth: `${MIN_FIELD_WIDTH}px`,
      width: `${width}px`,
      cursor: 'default',
      opacity: 0.8,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '1px solid #d1d5db',
      padding: '2px 4px',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      ...segment.style,
    }}
  />
);

/**
 * Dropdown renderer — rendered as read-only text input
 */
const DropdownSegmentRenderer: React.FC<{
  segment: any;
  highlighted: boolean;
  width: number;
}> = ({ segment, highlighted, width }) => (
  <input
    type="text"
    className={`inline-field${highlighted ? ' highlighted' : ''} read-only`}
    value={formatInlineValue(segment.value)}
    disabled
    readOnly
    aria-label={`${segment.paramName || 'Parameter'} (dropdown, read-only)`}
    style={{
      minWidth: `${MIN_FIELD_WIDTH}px`,
      width: `${width}px`,
      cursor: 'default',
      opacity: 0.8,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '1px solid #d1d5db',
      padding: '2px 4px',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      ...segment.style,
    }}
  />
);

/**
 * Table segment renderer
 */
const TableSegmentRenderer: React.FC<{
  segment: any;
  nodeId: string;
}> = ({ segment, nodeId }) => (
  <InlineTable
    segment={{ ...segment, disabled: true }}
    nodeId={nodeId}
    onParameterChanged={undefined}
  />
);

/**
 * Main segment renderer with error boundary
 */
export const SegmentRenderer: React.FC<SegmentRendererProps> = React.memo(
  ({
    segment: seg,
    nodeId,
    highlighted = false,
    onParameterChanged,
    readOnly = true,
  }) => {
    // Memoize field width calculation to avoid recalculation on every render
    const fieldWidth = useMemo(() => {
      if (seg.kind === 'field' || seg.kind === 'dropdown') {
        const fieldSeg = seg as any;
        return calculateFieldWidth(fieldSeg.value);
      }
      return MIN_FIELD_WIDTH;
    }, [seg.kind, (seg as any).value]);

    try {
      if (seg.kind === 'text') {
        return <TextSegment value={seg.value} />;
      }

      if (seg.kind === 'material-ref') {
        return (
          <MaterialRefSegment
            materialName={seg.materialName}
            containerName={seg.containerName}
            constituentExpr={seg.constituentExpr}
            constituents={seg.constituents}
            showConstituentToggle={seg.showConstituentToggle}
            renderVariant={seg.renderVariant}
          />
        );
      }

      if (seg.kind === 'field') {
        return (
          <FieldSegmentRenderer
            segment={seg}
            highlighted={highlighted}
            width={fieldWidth}
          />
        );
      }

      if (seg.kind === 'dropdown') {
        return (
          <DropdownSegmentRenderer
            segment={seg}
            highlighted={highlighted}
            width={fieldWidth}
          />
        );
      }

      if (seg.kind === 'table') {
        return (
          <TableSegmentRenderer
            segment={seg}
            nodeId={nodeId}
          />
        );
      }

      // Unhandled segment type - should not reach here due to exhaustive check
      console.warn(`Unknown segment kind encountered`);
      return <UnhandledSegmentFallback kind={'unknown'} />;
    } catch (error) {
      console.error('SegmentRenderer error:', error, { segment: seg, nodeId });
      return (
        <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>
          [Failed to render]
        </span>
      );
    }
  },
  (prevProps, nextProps) => {
    // Memoization: skip render if nothing important changed
    const prevSeg = prevProps.segment as any;
    const nextSeg = nextProps.segment as any;
    
    return (
      prevSeg.kind === nextSeg.kind &&
      prevSeg.value === nextSeg.value &&
      prevSeg.materialName === nextSeg.materialName &&
      prevSeg.containerName === nextSeg.containerName &&
      prevSeg.constituentExpr === nextSeg.constituentExpr &&
      JSON.stringify(prevSeg.constituents ?? []) === JSON.stringify(nextSeg.constituents ?? []) &&
      prevSeg.showConstituentToggle === nextSeg.showConstituentToggle &&
      prevSeg.renderVariant === nextSeg.renderVariant &&
      prevProps.nodeId === nextProps.nodeId &&
      prevProps.highlighted === nextProps.highlighted &&
      prevProps.readOnly === nextProps.readOnly
    );
  }
);

SegmentRenderer.displayName = 'SegmentRenderer';
